/**
 * 引擎輸出的 memo 化推導層。
 *
 * 這一層是「store ↔ 純展示元件」之間唯一允許呼叫 `packages/core` 引擎的地方。
 * 兩個硬性要求：
 *
 * 1. **絕不在 render path 重算**：所有引擎呼叫都以 `Project` 物件本身為 key
 *    快取（`WeakMap`）。`Project` 是不可變的，store 每次 commit 都會換一顆新的
 *    物件，因此「同一顆 project = 同一份結果」永遠成立，而舊 project 被 GC 時
 *    快取也跟著釋放，不會累積。
 * 2. **絕不拋錯**：引擎的正常業務錯誤（相依成環、缺基準線、日期格式異常……）
 *    一律降級成 `null` / 空集合，由呼叫端決定要顯示什麼引導語。整合層讓 UI
 *    崩潰是最糟的失敗模式。
 */

import {
  computeCost,
  computeEarnedValue,
  computeExpectedProgress,
  computeProjectProgressSummary,
  computeTimephasedWork,
  detectOverallocations,
  resolveCalendar,
  workingMinutesBetween,
  type BaselineNumber,
  type BaselineTaskSnapshot,
  type CostResult,
  type EacMethod,
  type EarnedValueMetrics,
  type EvmResult,
  type ExpectedProgress,
  type ISODateTime,
  type Overallocation,
  type Project,
  type ProjectProgressSummary,
} from '@bigint-project/core';

import type { DailyValue } from '../components/resource/timephased-grid';

/** 專案使用的主要基準線編號。 */
export const PRIMARY_BASELINE: BaselineNumber = 0;

/** EAC 比較用的三種方法（與 `EvmReport` 的 `eacComparison` 對應）。 */
export const EAC_METHODS: readonly EacMethod[] = ['Additive', 'CpiBased', 'CpiSpiBased'];

// ---------------------------------------------------------------------------
// memo 基礎設施
// ---------------------------------------------------------------------------

const caches = new WeakMap<Project, Map<string, unknown>>();

function memo<T>(project: Project, key: string, compute: () => T): T {
  let bucket = caches.get(project);
  if (bucket === undefined) {
    bucket = new Map<string, unknown>();
    caches.set(project, bucket);
  }
  if (bucket.has(key)) return bucket.get(key) as T;
  const value = compute();
  bucket.set(key, value);
  return value;
}

/** 執行引擎呼叫；任何拋出都降級為 `fallback`，並在 console 留一筆線索。 */
function guard<T>(label: string, compute: () => T, fallback: T): T {
  try {
    return compute();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn(`[integration] ${label} failed, degrading:`, error);
    return fallback;
  }
}

/** 供測試觀察 memo 是否真的生效。 */
export function isCached(project: Project, key: string): boolean {
  return caches.get(project)?.has(key) ?? false;
}

// ---------------------------------------------------------------------------
// 狀態日
// ---------------------------------------------------------------------------

/**
 * EVM / 成本計算要帶的狀態日；沒設定就**不帶**，讓引擎自己降級。
 *
 * 這裡刻意不補一個「現在」上去。引擎對缺 statusDate 已經有明確的降級語義
 * （退回 `Project.finishDate` 當結算點，並發 `EVM_NO_STATUS_DATE` diagnostic，
 * 見 `engine/evm-engine.ts`）。整合層若擅自塞一個當下時間進去，等於用一個
 * 使用者從沒指定過的值蓋掉那套語義，還會讓診斷永遠不觸發——UI 就再也無法提示
 * 「這份 EVM 是以完成日結算的」。
 */
export function statusDateOption(project: Project): ISODateTime | undefined {
  return project.statusDate ?? undefined;
}

/**
 * 預期進度／落後判定用的「現在」：有設狀態日就用狀態日，否則用本機當下時間
 * （本地 wall-clock，與排程日期同語義）。這與 `statusDateOption` 刻意不同：
 * 落後警示的需求本來就是「依當前日期反推」，沒有狀態日時不該沒有警示。
 */
export function progressAsOf(project: Project, now: Date = new Date()): ISODateTime {
  if (project.statusDate) return project.statusDate;
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}` as ISODateTime;
}

// ---------------------------------------------------------------------------
// 資源：過度分配
// ---------------------------------------------------------------------------

export function selectOverallocations(project: Project): readonly Overallocation[] {
  return memo(project, 'overallocations', () =>
    guard('detectOverallocations', () => detectOverallocations(project, 'Day'), []),
  );
}

const MS_PER_DAY = 86_400_000;

function eachDay(fromDate: string, toDate: string): readonly string[] {
  const out: string[] = [];
  let cursor = fromDate;
  // `to` 是不含端點的區間終點；`from === to` 時仍要標記該日（零長度區間亦為衝突）。
  if (cursor >= toDate) return [cursor];
  let guardCount = 0;
  while (cursor < toDate && guardCount < 10_000) {
    out.push(cursor);
    cursor = new Date(Date.parse(`${cursor}T00:00:00Z`) + MS_PER_DAY).toISOString().slice(0, 10);
    guardCount += 1;
  }
  return out;
}

/** resourceId → 該資源過度分配的日期集合（`YYYY-MM-DD`）。 */
export function selectOverallocatedDaysByResource(
  project: Project,
): ReadonlyMap<string, ReadonlySet<string>> {
  return memo(project, 'overallocatedDays', () => {
    const byResource = new Map<string, Set<string>>();
    for (const entry of selectOverallocations(project)) {
      const key = entry.resourceId as string;
      const set = byResource.get(key) ?? new Set<string>();
      for (const day of eachDay((entry.from as string).slice(0, 10), (entry.to as string).slice(0, 10))) {
        set.add(day);
      }
      byResource.set(key, set);
    }
    return byResource;
  });
}

export function selectOverallocatedResourceIds(project: Project): ReadonlySet<string> {
  return memo(
    project,
    'overallocatedResourceIds',
    () => new Set(selectOverallocatedDaysByResource(project).keys()),
  );
}

// ---------------------------------------------------------------------------
// 資源：逐日工時
// ---------------------------------------------------------------------------

/** assignmentId → 逐日工時。 */
export function selectDailyByAssignment(project: Project): ReadonlyMap<string, readonly DailyValue[]> {
  return memo(project, 'dailyByAssignment', () =>
    guard(
      'computeTimephasedWork',
      () => {
        const result = computeTimephasedWork(project, 'Day');
        const byAssignment = new Map<string, readonly DailyValue[]>();
        for (const entry of result.entries) {
          byAssignment.set(
            entry.assignmentId as string,
            entry.work.map((slice) => ({
              date: (slice.start as string).slice(0, 10),
              hours: (slice.valueMinutes as number) / 60,
            })),
          );
        }
        return byAssignment;
      },
      new Map<string, readonly DailyValue[]>(),
    ),
  );
}

// ---------------------------------------------------------------------------
// 成本
// ---------------------------------------------------------------------------

export function selectCostResult(project: Project): CostResult | null {
  return memo(project, 'cost', () =>
    guard(
      'computeCost',
      () =>
        computeCost({
          project,
          options: {
            baselineNumber: PRIMARY_BASELINE,
            timephasedGranularity: 'Days',
            statusDate: statusDateOption(project),
          },
        }),
      null,
    ),
  );
}

/** 目前成本（不做基準線比較），供總覽等不應被 Baseline 0 阻擋的畫面使用。 */
export function selectCurrentCostResult(project: Project): CostResult | null {
  return memo(project, 'currentCost', () =>
    guard(
      'computeCost(current)',
      () => computeCost({ project, options: { statusDate: statusDateOption(project) } }),
      null,
    ),
  );
}

// ---------------------------------------------------------------------------
// EVM
// ---------------------------------------------------------------------------

export function hasBaseline(project: Project): boolean {
  return project.baselines.some((baseline) => baseline.number === PRIMARY_BASELINE);
}

export function selectEvmResult(project: Project): EvmResult | null {
  return memo(project, 'evm', () =>
    guard(
      'computeEarnedValue',
      () =>
        computeEarnedValue(project, {
          statusDate: statusDateOption(project),
          baselineNumber: PRIMARY_BASELINE,
          eacMethod: 'Additive',
          tcpiTarget: 'BAC',
        }),
      null,
    ),
  );
}

export interface EacComparison {
  readonly method: EacMethod;
  readonly metrics: EarnedValueMetrics;
}

export function selectEacComparison(project: Project): readonly EacComparison[] {
  return memo(project, 'evmByMethod', () =>
    guard(
      'computeEarnedValue(eacComparison)',
      () =>
        EAC_METHODS.map((method) => ({
          method,
          metrics: computeEarnedValue(project, {
            statusDate: statusDateOption(project),
            baselineNumber: PRIMARY_BASELINE,
            eacMethod: method,
            tcpiTarget: 'BAC',
          }).project,
        })),
      [],
    ),
  );
}

// ---------------------------------------------------------------------------
// 基準線快照索引
// ---------------------------------------------------------------------------

export function selectBaselineTaskSnapshots(
  project: Project,
): ReadonlyMap<string, BaselineTaskSnapshot> {
  return memo(project, 'baselineTasks', () => {
    const baseline = project.baselines.find((b) => b.number === PRIMARY_BASELINE);
    const byTask = new Map<string, BaselineTaskSnapshot>();
    if (baseline === undefined) return byTask;
    for (const snapshot of baseline.tasks) byTask.set(snapshot.taskId as string, snapshot);
    return byTask;
  });
}

// ---------------------------------------------------------------------------
// 任務 → 資源名稱
// ---------------------------------------------------------------------------

/** taskId → `"Alice, Bob"`；沒有指派則為空字串。 */
export function selectResourceNamesByTask(project: Project): ReadonlyMap<string, string> {
  return memo(project, 'resourceNamesByTask', () => {
    const nameById = new Map<string, string>();
    for (const resource of project.resources) nameById.set(resource.id as string, resource.name);
    const byTask = new Map<string, string[]>();
    for (const assignment of project.assignments) {
      const key = assignment.taskId as string;
      const list = byTask.get(key) ?? [];
      list.push(nameById.get(assignment.resourceId as string) ?? String(assignment.resourceId));
      byTask.set(key, list);
    }
    const out = new Map<string, string>();
    for (const [taskKey, names] of byTask) out.set(taskKey, names.join(', '));
    return out;
  });
}

// ---------------------------------------------------------------------------
// 預期進度 / 落後
// ---------------------------------------------------------------------------

/**
 * taskId → 預期進度。表格、甘特圖與工時檢視都要逐任務判斷落後，各自呼叫
 * `computeExpectedProgress()` 會讓每一列各建一次日曆快取（O(n²)）；這裡一次
 * 走完全部任務並 memo，key 帶 `asOf` 是因為同一顆 project 在跨日／改狀態日後
 * 的答案不同。
 */
export function selectExpectedProgressByTask(
  project: Project,
  asOf: ISODateTime,
): ReadonlyMap<string, ExpectedProgress> {
  return memo(project, `expectedProgress:${asOf as string}`, () =>
    guard(
      'computeExpectedProgress',
      () => {
        const byTask = new Map<string, ExpectedProgress>();
        for (const task of project.tasks) {
          byTask.set(task.id as string, computeExpectedProgress(project, task, asOf));
        }
        return byTask;
      },
      new Map<string, ExpectedProgress>(),
    ),
  );
}

/** 專案層級的落後盤點（只計葉節點，見 core 的 `computeProjectProgressSummary`）。 */
export function selectProjectProgressSummary(
  project: Project,
  asOf: ISODateTime,
): ProjectProgressSummary {
  return memo(project, `progressSummary:${asOf as string}`, () =>
    guard(
      'computeProjectProgressSummary',
      () => computeProjectProgressSummary(project, asOf),
      {
        asOf,
        total: 0,
        counts: { notStarted: 0, behind: 0, onTrack: 0, ahead: 0, complete: 0 },
        behindTaskIds: [],
      },
    ),
  );
}

/**
 * 判斷某一天（`YYYY-MM-DD`）是否為工作日，依專案的預設日曆。
 *
 * 工時網格只用它把非工作日的欄位淡化 —— 刻意不逐資源解析各自的日曆：
 * 那會讓一張週表每格都要重解日曆，而使用者在這裡要的只是「哪幾天是週末」。
 */
export function selectIsWorkingDay(project: Project): (date: string) => boolean {
  return memo(project, 'isWorkingDay', () =>
    guard(
      'resolveCalendar(default)',
      () => {
        const byId = new Map(project.calendars.map((calendar) => [calendar.id as string, calendar]));
        const definition = byId.get(project.defaultCalendarId as string);
        if (definition === undefined) return () => true;
        const resolved = resolveCalendar(definition, byId);
        const cache = new Map<string, boolean>();
        return (date: string): boolean => {
          const cached = cache.get(date);
          if (cached !== undefined) return cached;
          const working = (workingMinutesBetween(
            resolved,
            `${date}T00:00:00` as ISODateTime,
            `${date}T23:59:59` as ISODateTime,
          ) as number) > 0;
          cache.set(date, working);
          return working;
        };
      },
      () => true,
    ),
  );
}
